import { useMemo } from "react";
import FormItem from "@components/FormItem/FormItem";
import BunkerBargeIMOField from "@features/vessel-call/vessel-call-status/PortServices/form/bunker-barge/BunkerBargeIMOField";
import type { PortServicesFormData } from "@features/vessel-call/vessel-call-status/PortServices/schema/portServicesFormSchema";
import { usePortServicesFormStore } from "@features/vessel-call/vessel-call-status/PortServices/store/usePortServicesForm.store";
import { useFormattedReference } from "@hooks/useFormattedReference";
import { useOffshoreServiceCompanies } from "@hooks/useOffshoreServiceCompanies";
import { REFERENCE_DATA_TYPES } from "@services/referenceDataApi";
import { type FromToLocation, type UnitsOfMeasurement } from "@services/vessel-call/types";
import { FormProvider, type UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";

type DebunkeringFieldsOutlet = {
  formData: UseFormReturn<PortServicesFormData>;
  updateFormData: (key: string, value: unknown) => void;
  currentVesselImoNumber?: string;
  selectedPortCode?: string;
};

const DebunkeringFields = ({ langBase }: { langBase: string }) => {
  const { formData, updateFormData, currentVesselImoNumber, selectedPortCode } =
    useOutletContext<DebunkeringFieldsOutlet>();
  const { t } = useTranslation();
  const { serviceCategoryCode, serviceTypeCode, serviceSubTypeCode } = usePortServicesFormStore();

  const { options: locations = [] } = useFormattedReference<FromToLocation>(
    REFERENCE_DATA_TYPES.FROM_TO_LOCATIONS,
    "bff-vessel-call",
  );

  const { options: offshoreCompanies = [] } = useOffshoreServiceCompanies(
    {
      portServiceCategoryCode: serviceCategoryCode,
      portServiceTypeCode: serviceTypeCode,
      portServiceSubtypeCode: serviceSubTypeCode,
    },
    "bff-vessel",
  );

  const { options: unitsOfMeasurement } = useFormattedReference<UnitsOfMeasurement>(
    REFERENCE_DATA_TYPES.UNITS_OF_MEASUREMENT,
    "bff-vessel-call",
  );

  const debunkeringTypeOptions = useMemo(
    () =>
      locations
        .filter(
          ({ portCode, fromToLocationCode }) =>
            portCode === selectedPortCode || fromToLocationCode === "SEA",
        )
        .map(({ fromToLocationCode, locationName }) => ({
          label: locationName,
          value: fromToLocationCode,
        })),
    [locations, selectedPortCode],
  );

  const companyOptions = useMemo(
    () =>
      offshoreCompanies
        .map(({ companyNameEnglish, companyCode }) => companyNameEnglish ?? companyCode ?? "")
        .filter((companyName) => companyName.trim().length > 0)
        .map((companyName) => ({
          label: companyName,
          value: companyName,
        })),
    [offshoreCompanies],
  );

  const unitOfMeasurementOptions = useMemo(
    () =>
      unitsOfMeasurement
        .filter((u) => u.isCargoUnit)
        .map((u) => ({ label: u.unitDescription, value: u.unitDescription }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [unitsOfMeasurement],
  );

  return (
    <FormProvider {...formData}>
      <div className="grid grid-cols-4 gap-2">
        <div className="order-1 col-span-2">
          <FormItem<PortServicesFormData, "bunkeringType">
            name="bunkeringType"
            type="autocomplete"
            options={debunkeringTypeOptions}
            label={t(`${langBase}.fields.debunkering-type`)}
            testId="debunkering-type"
            onFieldChange={updateFormData}
          />
        </div>

        <div
          data-testid="debunkering-bunker-barge-layout"
          className="contents [&>*:first-child]:order-2 [&>*:last-child]:order-4 [&>*:last-child]:col-start-1"
        >
          <BunkerBargeIMOField
            formData={formData}
            updateFormData={updateFormData}
            langBase={langBase}
            currentVesselImoNumber={currentVesselImoNumber}
            selectedPortCode={selectedPortCode}
            testId="debunkering-bunkerBargeIMO"
          />
        </div>

        <div className="order-3 col-span-2">
          <FormItem<PortServicesFormData, "bunkeringCompanyName">
            testId="debunkeringCompanyName"
            name="bunkeringCompanyName"
            type="autocomplete"
            options={companyOptions}
            label={t(`${langBase}.fields.debunkering-company-name`)}
            onFieldChange={updateFormData}
            enforceUppercase
          />
        </div>
        <div className="order-5 col-span-1 col-start-1">
          <FormItem<PortServicesFormData, "quantity">
            name="quantity"
            type="number"
            label={t(`${langBase}.fields.quantity`)}
            testId="debunkering-quantity"
            onFieldChange={updateFormData}
            maxDigits={15}
            canBeginWithZero={false}
          />
        </div>
        <div className="order-6 col-span-1">
          <FormItem<PortServicesFormData, "unitOfMeasurement">
            name="unitOfMeasurement"
            type="autocomplete"
            options={unitOfMeasurementOptions}
            label={t(`${langBase}.fields.unit-of-measurement`)}
            testId="debunkering-unit-of-measurement"
            onFieldChange={updateFormData}
          />
        </div>

        <div className="order-7 col-span-2 col-start-1">
          <FormItem<PortServicesFormData, "rts">
            name="rts"
            type="date-picker"
            label={t(`${langBase}.fields.rts`)}
            testId="debunkering-rts"
            onFieldChange={updateFormData}
            timePicker
            combinedInput
            dateConstraint="not-past"
            minDate={new Date()}
            maxDate={new Date(Date.now() + 24 * 60 * 60 * 1000)}
          />
        </div>
      </div>
    </FormProvider>
  );
};

export default DebunkeringFields;
